// ── The Sessions page (Sessions overview) ───────────────────────
//
// MOVED OUT OF app.js UNCHANGED (standing rule ③: extract what you touch, and
// MOVE and CHANGE are separate commits): the client drawer, the client panel,
// and the window.* handlers their buttons call. app.js keeps
// window.logSessionForClient (it sets app.js's own profileClientId), hands
// over its `clients` array with setSessionsOverviewDeps, and repaints this page
// with repaintSessionsOverview when the session data changes.

import * as _sessions from './sessions.js';
import { escHtml, formatDate } from './utils.js';
import { showClientProfile } from './navigation.js';

/** @type {{ getClients: () => any[] }} */
let _deps = { getClients: () => [] };

/** @param {Partial<typeof _deps>} d */
export function setSessionsOverviewDeps(d) { _deps = { ..._deps, ...d }; }

/** app.js's own roster array (the VISIBLE set, not state.js's). */
const clientsList = () => _deps.getClients() || [];

/** Repaint the drawer and the open client's panel, as the session listener did in app.js. */
export function repaintSessionsOverview() {
  renderSessDrawerList();
  if (_sessDetailClientId) renderSessDetail(_sessDetailClientId);
  scheduleCounts();
}

// ⚠️ ONE COUNT (one sessions system, part 7): where the switch is on, this page's numbers are the server's
// (_sessions.sessionsFree / sessionCounts), the same as the Session Tracker, the Gym Mobile View and both apps. They
// reload once for a burst of session changes, then the page repaints. Off: nothing loads, the page counts as before.
/** @type {ReturnType<typeof setTimeout>|undefined} */
let _countsTimer;
function scheduleCounts(delay = 400) {
  clearTimeout(_countsTimer);
  _countsTimer = setTimeout(() => {
    _sessions.loadSessionCounts().then((loaded) => {
      if (!loaded) return;
      renderSessDrawerList();
      if (_sessDetailClientId) renderSessDetail(_sessDetailClientId);
    }, (err) => console.warn('[sessions] the count did not load:', err.message));
  }, delay);
}

let _sessDetailClientId = null;
let _svSearch = '';

// ── Shared helpers ────────────────────────────────────────────
function _clientInitials(c) {
  const f = (c.firstName || '').trim(), l = (c.lastName || '').trim();
  if (f && l) return (f[0] + l[0]).toUpperCase();
  const parts = (c.name || '').trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : (parts[0] || '??').slice(0, 2).toUpperCase();
}
function _avatarColor(id) {
  const COLORS = ['#7c3aed','#2563eb','#059669','#d97706','#dc2626','#0891b2','#65a30d','#c026d3','#ea580c','#0d9488'];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = id.charCodeAt(i) + ((h << 5) - h);
  return COLORS[Math.abs(h) % COLORS.length];
}
function _lastSessionStr(clientId) {
  const logs = _sessions.getSessionLogs()
    .filter(s => s.clientId === clientId && s.date)
    .sort((a, b) => b.date.localeCompare(a.date));
  return logs.length ? `Last: ${formatDate(logs[0].date)}` : 'No sessions yet';
}

export function renderSessionsView() {
  renderSessDrawerList();
  if (_sessDetailClientId) renderSessDetail(_sessDetailClientId);
  // ⚠️ THE POOL IS A LOGIN-TIME SNAPSHOT until the session tables are in the
  // realtime publication — see refreshSessionData. Paint from what we have so
  // the view is never blank, then re-read and let the callback repaint. A
  // coach opening this page after the 6am cron was being shown last night's
  // numbers (2026-08-27).
  _sessions.refreshSessionData();
  scheduleCounts(0);
}

function renderSessDrawerList() {
  const el = document.getElementById('sv-drawer-list');
  if (!el) return;
  // In-person and hybrid clients, and an online client who has had a session pack (10/16)
  const all = clientsList()
    .filter(c => c.status === 'active' && _sessions.sessionsShownFor(c))
    .sort((a, b) => (a.firstName || a.name || '').localeCompare(b.firstName || b.name || ''));

  const totalEl = document.getElementById('sv-panel-total');
  if (totalEl) totalEl.textContent = all.length;

  if (!all.length) {
    el.innerHTML = `<div class="sv-drawer-empty">No active clients with sessions.</div>`;
    return;
  }

  function itemHtml(c, showIssue = false) {
    const bal      = _sessions.sessionsFree(c.id);
    const expiring = _sessions.sessionExpiringSoon(c.id, 14);
    const balCls   = bal === 0 ? 'sv-drawer-bal--zero' : expiring > 0 ? 'sv-drawer-bal--warn' : 'sv-drawer-bal--ok';
    const name     = escHtml(c.firstName && c.lastName ? `${c.firstName} ${c.lastName}` : c.name || 'Unknown');
    const active   = _sessDetailClientId === c.id ? ' active' : '';
    const initials = _clientInitials(c);
    const color    = _avatarColor(c.id);
    const lastDate = _lastSessionStr(c.id);
    const issueBtn = showIssue
      ? `<span class="sv-issue-btn" onclick="event.stopPropagation();window.issueCreditsForClient('${c.id}')">+ Issue</span>`
      : '';
    return `<button class="sv-drawer-item${active}" onclick="window.selectSessClient('${c.id}')">
      <span class="sv-item-avatar" style="background:${color}">${initials}</span>
      <span class="sv-item-body">
        <span class="sv-drawer-item-name">${name}</span>
        <span class="sv-item-last">${lastDate}</span>
      </span>
      ${issueBtn}
      <span class="sv-drawer-bal ${balCls}">${bal}</span>
    </button>`;
  }

  // Flat search results — no accordion when filtering
  if (_svSearch) {
    const q = _svSearch.toLowerCase();
    const filtered = all.filter(c => {
      const full = (c.firstName || '') + ' ' + (c.lastName || '') + (c.name || '');
      return full.toLowerCase().includes(q);
    });
    el.innerHTML = filtered.length
      ? filtered.map(c => itemHtml(c, _sessions.sessionsFree(c.id) === 0)).join('')
      : `<div class="sv-drawer-empty">No clients match "${escHtml(_svSearch)}"</div>`;
    return;
  }

  const withCredits = all.filter(c => _sessions.sessionsFree(c.id) > 0);
  const noCredits   = all.filter(c => _sessions.sessionsFree(c.id) === 0);
  const chevron     = `<svg class="sv-group-chevron" viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M7 10l5 5 5-5z"/></svg>`;

  el.innerHTML = `
    <div class="sv-group">
      <button class="sv-group-header" onclick="window.toggleSvGroup(this)">
        <span class="sv-group-count">${withCredits.length}</span>
        <span class="sv-group-label">Has sessions</span>
        ${chevron}
      </button>
      <div class="sv-group-items">
        ${withCredits.length ? withCredits.map(c => itemHtml(c, false)).join('') : '<div class="sv-drawer-empty">None</div>'}
      </div>
    </div>
    <div class="sv-group collapsed">
      <button class="sv-group-header" onclick="window.toggleSvGroup(this)">
        <span class="sv-group-count">${noCredits.length}</span>
        <span class="sv-group-label">No credits</span>
        ${chevron}
      </button>
      <div class="sv-group-items">
        ${noCredits.length ? noCredits.map(c => itemHtml(c, true)).join('') : '<div class="sv-drawer-empty">None</div>'}
      </div>
    </div>`;
}

function renderSessDetail(clientId) {
  const noClient = document.getElementById('sv-no-client');
  const detail   = document.getElementById('sv-detail');
  const subtitle = document.getElementById('sv-subtitle');
  if (!detail) return;

  const c = clientsList().find(x => x.id === clientId);
  if (!c) return;

  if (noClient) noClient.classList.add('hidden');
  detail.classList.remove('hidden');

  const name       = escHtml(c.firstName && c.lastName ? `${c.firstName} ${c.lastName}` : c.name || 'Unknown');
  const svcLabel   = c.serviceType === 'hybrid' ? 'Hybrid' : c.serviceType === 'online' ? 'Online' : 'In-Person';
  const sessPerMo  = _sessions.getMonthlySessionCount(c);
  if (subtitle) subtitle.textContent = `${name} · ${svcLabel}${sessPerMo > 0 ? ` · ${sessPerMo} sessions/mo` : ''}`;

  const bal        = _sessions.sessionsFree(clientId);
  const counts     = _sessions.sessionCounts(clientId);
  const reschedits = _sessions.getSessionReschedits().filter(r => r.clientId === clientId);
  const credits    = _sessions.getSessionCredits().filter(p => p.clientId === clientId);
  const logs       = _sessions.getSessionLogs()
    .filter(l => l.clientId === clientId)
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  const availResched = reschedits.filter(r => r.status === 'available' && !_sessIsExpired(r.expiryDate)).length;
  const availIssued  = _sessions.availableSessionCredits(clientId).reduce((s, p) => s + p.remaining, 0);
  const expiring     = _sessions.sessionExpiringSoon(clientId, 14);
  const balCls       = bal === 0 ? 'sv-bal--zero' : expiring > 0 ? 'sv-bal--warn' : 'sv-bal--ok';

  let html = `
    <div class="sv-client-header">
      <div class="sv-client-name">${name}</div>
      <div style="display:flex;gap:8px">
        <button class="btn-secondary btn-sm" onclick="window.showClientProfile('${c.id}')">View Full Profile</button>
        <button class="btn-secondary btn-sm" onclick="window.openIssueCreditsModal('${c.id}')" data-sl-retire>Issue Credits</button>
        <button class="btn-secondary btn-sm" data-owner-only data-sl-only onclick="window.openSessionsAdd('${c.id}', this)">+ Add sessions</button>
        <button class="btn-primary btn-sm" onclick="window.logSessionForClient('${c.id}')"
          ${bal === 0 ? 'disabled' : ''}>Log Session</button>
      </div>
    </div>
    <div class="sv-summary-strip">${counts ? `
      <div class="sv-summary-tile">
        <span class="sv-summary-num ${balCls}">${counts.free}</span>
        <span class="sv-summary-label">Available</span>
      </div>
      <div class="sv-summary-tile">
        <span class="sv-summary-num">${counts.bookedAll ?? counts.setAside ?? 0}</span>
        <span class="sv-summary-label">Booked</span>
      </div>
      <div class="sv-summary-tile">
        <span class="sv-summary-num">${counts.used ?? 0}</span>
        <span class="sv-summary-label">Used</span>
      </div>` : `
      <div class="sv-summary-tile">
        <span class="sv-summary-num ${balCls}">${bal}</span>
        <span class="sv-summary-label">Total Credits</span>
      </div>
      <div class="sv-summary-tile">
        <span class="sv-summary-num">${availIssued}</span>
        <span class="sv-summary-label">Issued</span>
      </div>
      <div class="sv-summary-tile">
        <span class="sv-summary-num">${availResched}</span>
        <span class="sv-summary-label">Makeup</span>
      </div>`}
      ${expiring > 0 ? `<div class="sv-summary-tile">
        <span class="sv-summary-num sv-bal--warn">${expiring}</span>
        <span class="sv-summary-label">Expiring Soon</span>
      </div>` : ''}
    </div>`;

  // Makeup credits section
  const reschedSorted = [...reschedits].sort((a, b) => (a.expiryDate || '').localeCompare(b.expiryDate || ''));
  html += `<div class="card sv-section">
    <div class="card-header sv-section-header" onclick="window.toggleSvSection('sv-sec-resched')">
      <h2>Makeup Credits <span class="sv-section-badge">${availResched} available</span></h2>
      <span class="sv-section-chev" id="sv-chev-resched">▼</span>
    </div>
    <div id="sv-sec-resched">`;
  if (!reschedSorted.length) {
    html += `<div class="sv-sec-empty">No makeup credits yet.</div>`;
  } else {
    reschedSorted.forEach(r => {
      // ⚠️ 'consumed' IS THE WORD THE SERVER WRITES — never 'used'.
      // session_reschedits carries exactly two statuses, both from
      // scheduling-helpers.js: 'available' and 'consumed'. Testing 'used'
      // made this ALWAYS false, so a SPENT make-up credit rendered live:
      // a "{N}d left" badge under a header correctly saying "0 available",
      // and inside 14 days it went yellow-urgent — the card shouting about
      // a credit nobody can spend (2026-08-26).
      // ⚠️ THIS LOGIC EXISTS IN THREE PLACES (app.js, gym.js,
      // modules/sessions.js). Change one, change all three.
      const used    = r.status === 'consumed';
      const expired = !used && _sessIsExpired(r.expiryDate);
      const dimmed  = used || expired;
      const days    = _sessDaysUntil(r.expiryDate);
      const badge   = used ? 'Used' : expired ? 'Expired' : `${days}d left`;
      const badgeCls = used ? 'tag-chip tag-chip--neutral' : expired ? 'tag-chip tag-chip--red' : days <= 14 ? 'tag-chip tag-chip--yellow' : 'tag-chip tag-chip--purple';
      html += `<div class="sv-sec-row${dimmed ? ' sv-sec-row--dim' : ''}">
        <div class="sv-sec-row-icon">🔄</div>
        <div class="sv-sec-row-body">
          <div class="sv-sec-row-title">${r.source === 'manual' ? `Issued by hand on ${formatDate(r.originalSessionDate)}` : `From session on ${formatDate(r.originalSessionDate)}`}</div>
          <div class="sv-sec-row-sub">Expires ${formatDate(r.expiryDate)}${r.cancelledBy ? ' · ' + (r.cancelledBy === 'client' ? 'Client cancelled' : 'Coach cancelled') : ''}${r.notes ? ' · ' + escHtml(r.notes) : ''}</div>
        </div>
        <span class="${badgeCls}">${badge}</span>
      </div>`;
    });
  }
  html += `</div></div>`;

  // Issued credits section
  const creditsSorted = [...credits].sort((a, b) => (b.issuedDate || '').localeCompare(a.issuedDate || ''));
  html += `<div class="card sv-section">
    <div class="card-header sv-section-header" onclick="window.toggleSvSection('sv-sec-issued')">
      <h2>Issued Credits <span class="sv-section-badge">${availIssued} remaining</span></h2>
      <span class="sv-section-chev" id="sv-chev-issued">▼</span>
    </div>
    <div id="sv-sec-issued">`;
  if (!creditsSorted.length) {
    html += `<div class="sv-sec-empty">No issued credits yet.</div>`;
  } else {
    creditsSorted.forEach(p => {
      const exhausted = p.remaining === 0;
      const expired   = !exhausted && _sessIsExpired(p.expiryDate);
      const dimmed    = exhausted || expired;
      const pct       = p.qty > 0 ? Math.round((p.remaining / p.qty) * 100) : 0;
      const label     = escHtml(p.periodLabel || p.periodDisplay || formatDate(p.issuedDate));
      const badge     = exhausted ? 'Exhausted' : expired ? 'Expired' : `${p.remaining} left`;
      const badgeCls  = (exhausted || expired) ? 'tag-chip tag-chip--neutral' : pct > 50 ? 'tag-chip tag-chip--blue' : pct > 20 ? 'tag-chip tag-chip--yellow' : 'tag-chip tag-chip--red';
      html += `<div class="sv-sec-row${dimmed ? ' sv-sec-row--dim' : ''}">
        <div class="sv-sec-row-icon">🎟</div>
        <div class="sv-sec-row-body">
          <div class="sv-sec-row-title">${label}</div>
          ${p.expiryDate ? `<div class="sv-sec-row-sub">Expires ${formatDate(p.expiryDate)}</div>` : ''}
        </div>
        <span class="${badgeCls}">${badge}</span>
      </div>`;
    });
  }
  html += `</div></div>`;

  // Session history section
  const recent = logs.slice(0, 30);
  html += `<div class="card sv-section">
    <div class="card-header sv-section-header" onclick="window.toggleSvSection('sv-sec-history')">
      <h2>Session History <span class="sv-section-badge">${logs.length} total</span></h2>
      <span class="sv-section-chev" id="sv-chev-history">▼</span>
    </div>
    <div id="sv-sec-history">`;
  if (!recent.length) {
    html += `<div class="sv-sec-empty">No sessions logged yet.</div>`;
  } else {
    recent.forEach(l => {
      const icon     = l.outcome === 'completed' ? '✅' : l.outcome === 'resched' ? '🔄' : '❌';
      const outcomeLabel = l.outcome === 'completed' ? 'Completed' : l.outcome === 'resched' ? 'Rescheduled' : 'Forfeit';
      const days     = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
      const d        = new Date((l.date || '') + 'T00:00:00');
      const dayLabel = l.date ? `${days[d.getDay()]} · ${formatDate(l.date)}` : '—';
      const badgeCls = l.outcome === 'completed' ? 'tag-chip tag-chip--green' : l.outcome === 'resched' ? 'tag-chip tag-chip--blue' : 'tag-chip tag-chip--red';
      html += `<div class="sv-sec-row">
        <div class="sv-sec-row-icon">${icon}</div>
        <div class="sv-sec-row-body">
          <div class="sv-sec-row-title">${dayLabel}</div>
          ${l.notes ? `<div class="sv-sec-row-sub">${escHtml(l.notes)}</div>` : ''}
        </div>
        <span class="${badgeCls}">${outcomeLabel}</span>
      </div>`;
    });
    if (logs.length > 30) html += `<div class="sv-sec-empty">${logs.length - 30} older sessions not shown</div>`;
  }
  html += `</div></div>`;

  detail.innerHTML = html;
}

window.selectSessClient = function(clientId) {
  _sessDetailClientId = clientId;
  renderSessDrawerList(); // refresh active highlight
  renderSessDetail(clientId);
};

window.toggleSvGroup = function(btn) {
  btn.closest('.sv-group').classList.toggle('collapsed');
};

window.onSvSearch = function(val) {
  _svSearch = val.trim();
  renderSessDrawerList();
};

window.issueCreditsForClient = function(clientId) {
  showClientProfile(clientId);
};

window.toggleSvSection = function(id) {
  const body = document.getElementById(id);
  const key  = id.replace('sv-sec-', '');
  const chev = document.getElementById(`sv-chev-${key}`);
  if (!body) return;
  const collapsed = body.classList.toggle('sv-collapsed');
  if (chev) chev.textContent = collapsed ? '▶' : '▼';
};

function _sessIsExpired(s) { return s ? s < new Date().toISOString().slice(0, 10) : false; }
function _sessDaysUntil(s) {
  if (!s) return Infinity;
  return Math.ceil((new Date(s + 'T00:00:00') - new Date(new Date().toISOString().slice(0,10) + 'T00:00:00')) / 86400000);
}
